import { useEffect, useRef, useState } from 'react'
import { displayTitle } from '../../../lib/displayName'
import { commands, commandPayload } from '../../../lib/runtime'
import { useMarketplaceOrderSnapshot } from '../../../generated/prototypeRuntime'
import { boardSchema, savedValue, dayKey, type Board } from './model'

export function useKitchenBoard(profileId: string) {
 const [board, setBoard] = useState<Board | null>(null)
 const [error, setError] = useState('')
 const [loading, setLoading] = useState(true)
 const [attempt, setAttempt] = useState(0)
 const [saving, setSaving] = useState(false)
 const pending = useRef(false)
 const retryKey = useRef({ body: '', key: '' })
 const orders = useMarketplaceOrderSnapshot() as { records?: readonly unknown[] }
 const reload = () => setAttempt((n) => n + 1)
 useEffect(() => {
  let active = true, inFlight = false
  const load = async () => {
   if (!profileId || inFlight || pending.current) return
   inFlight = true
   try {
    const response = commandPayload(await commands.menu.getKitchenBoard(dayKey()))
    const parsed = boardSchema.parse(response.board)
    // Names read the same here as on every customer card, however they were typed.
    const next = {
     ...parsed,
     dishes: parsed.dishes.map((dish) => ({ ...dish, name: displayTitle(dish.name) })),
     orders: parsed.orders.map((order) => ({ ...order, items: order.items.map((item) => ({ ...item, name: displayTitle(item.name) })) })),
    }
    if (next.chef_profile_id !== profileId) throw new Error('Wrong chef board')
    if (active) { setBoard(next); setError('') }
   } catch {
    if (active) setError('Could not refresh the kitchen board. Refresh before making more changes.')
   } finally { inFlight = false; if (active) setLoading(false) }
  }
  const initial = window.setTimeout(() => { void load() }, 100)
  const refresh = () => { if (document.visibilityState !== 'hidden') void load() }
  const timer = window.setInterval(refresh, 15000)
  window.addEventListener('focus', refresh)
  return () => { active = false; clearTimeout(initial); clearInterval(timer); window.removeEventListener('focus', refresh) }
 }, [profileId, attempt, orders?.records])
 const save = async (kind: 'plan' | 'stock', payload: Record<string, unknown>) => {
  if (pending.current) return false
  pending.current = true; setSaving(true)
  const body = JSON.stringify({ kind, ...payload })
  if (retryKey.current.body !== body) retryKey.current = { body, key: crypto.randomUUID() }
  try {
   const result = commandPayload(await (kind === 'plan' ? commands.menu.saveKitchenPlan : commands.menu.saveKitchenStock)(payload, retryKey.current.key))
   const value = savedValue(result, kind)
   setBoard((prev) => !prev ? prev : 'dish_id' in value
    ? { ...prev, plans: [...prev.plans.filter((p) => !(p.location_id === value.location_id && p.day === value.day && p.dish_id === value.dish_id)), value] }
    : { ...prev, stock: [...prev.stock.filter((s) => !(s.location_id === value.location_id && s.item_key === value.item_key)), value] })
   retryKey.current = { body: '', key: '' }
   reload()
   return true
  } catch {
   setError('That save did not complete. Reload the board and try again.')
   return false
  } finally { pending.current = false; setSaving(false) }
 }
 const orderChanged = (id: string, status: string) => {
  setBoard((prev) => prev ? { ...prev, orders: prev.orders.map((o) => o.id === id ? { ...o, status } : o) } : prev)
  reload()
 }
 return { orderChanged, board: board?.chef_profile_id === profileId ? board : null, error, loading, saving, save, reload }
}
